import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import { requireManager } from './role-guard'

/**
 * Staff billing view of guest stays: what each party owes, and the ways the
 * lodge collects it — a card/bank link the guest opens themselves, or a check
 * or cash settlement recorded at the dock.
 */

export type StayCharge = {
  id: string
  label: string
  amount_cents: number
  note: string | null
  charge_date: string | null
  created_at: string
}

export type StayRow = {
  id: string
  name: string
  email: string
  phone: string | null
  arrival_date: string | null
  departure_date: string | null
  party_size: number | null
  package: string | null
  confirmation_code: string | null
  order_id: string | null
  order_status: string | null
  order_total_cents: number
  order_items: { name?: string; qty?: number; unit_price_cents?: number }[]
  charges: StayCharge[]
  charges_total_cents: number
  balance_cents: number
}

type Ctx = { supabase: { from: (t: string) => any }; userId: string }

async function assertAdmin(context: unknown) {
  const { supabase, userId } = await requireManager(context)
  return { supabase, userId }
}

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** Every approved stay, with its order and extras rolled into a balance. */
export const listStayBilling = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ stays: StayRow[] }> => {
    const { supabase } = await assertAdmin(context)

    const { data: bookings, error } = await supabase
      .from('booking_requests')
      .select(
        'id,name,email,phone,arrival_date,departure_date,party_size,package,confirmation_code',
      )
      .eq('status', 'approved')
      .order('arrival_date', { ascending: true })
      .limit(500)
    if (error) throw new Error(error.message)

    const ids = (bookings ?? []).map((b: { id: string }) => b.id)
    if (ids.length === 0) return { stays: [] }

    const [orders, charges] = await Promise.all([
      supabase
        .from('shop_orders')
        .select('id,booking_request_id,status,total_cents,items')
        .in('booking_request_id', ids),
      supabase
        .from('booking_charges')
        .select('id,booking_id,label,amount_cents,note,charge_date,created_at')
        .in('booking_id', ids),
    ])

    const orderByBooking = new Map<string, any>()
    for (const o of orders.data ?? []) orderByBooking.set(o.booking_request_id, o)
    const chargesByBooking = new Map<string, StayCharge[]>()
    for (const c of charges.data ?? []) {
      const list = chargesByBooking.get(c.booking_id) ?? []
      list.push(c as StayCharge)
      chargesByBooking.set(c.booking_id, list)
    }

    const stays: StayRow[] = (bookings ?? []).map((b: any) => {
      const order = orderByBooking.get(b.id)
      const list = (chargesByBooking.get(b.id) ?? []).sort((x, y) =>
        (x.charge_date ?? x.created_at).localeCompare(y.charge_date ?? y.created_at),
      )
      const chargesTotal = list.reduce((sum, c) => sum + c.amount_cents, 0)
      const orderTotal = Number(order?.total_cents ?? 0)
      const paid = (order?.status ?? '').toLowerCase() === 'paid'
      return {
        id: b.id,
        name: b.name,
        email: b.email,
        phone: b.phone ?? null,
        arrival_date: b.arrival_date,
        departure_date: b.departure_date,
        party_size: b.party_size,
        package: b.package,
        confirmation_code: b.confirmation_code,
        order_id: order?.id ?? null,
        order_status: order?.status ?? null,
        order_total_cents: orderTotal,
        order_items: Array.isArray(order?.items) ? order.items : [],
        charges: list,
        charges_total_cents: chargesTotal,
        balance_cents: paid ? 0 : orderTotal + chargesTotal,
      }
    })

    return { stays }
  })

/** Emails the guest their balance with a card/bank link and check/cash options. */
export const sendStayPaymentRequest = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        bookingId: z.string().uuid(),
        message: z.string().trim().max(600).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = await assertAdmin(context)

    const { data: booking } = await supabase
      .from('booking_requests')
      .select('id,name,email,arrival_date,departure_date,confirmation_code')
      .eq('id', data.bookingId)
      .maybeSingle()
    if (!booking?.email) return { ok: false, reason: 'no_email' as const }

    const [{ data: order }, { data: charges }] = await Promise.all([
      supabase
        .from('shop_orders')
        .select('items,total_cents,status')
        .eq('booking_request_id', booking.id)
        .maybeSingle(),
      supabase
        .from('booking_charges')
        .select('label,amount_cents')
        .eq('booking_id', booking.id),
    ])

    const lines: { name: string; amount: string }[] = []
    let total = 0
    for (const item of (Array.isArray(order?.items) ? order.items : []) as any[]) {
      const amount = Math.round(Number(item.qty ?? 1) * Number(item.unit_price_cents ?? 0))
      if (amount <= 0) continue
      total += amount
      lines.push({ name: String(item.name ?? 'Stay charge'), amount: money(amount) })
    }
    for (const charge of (charges ?? []) as { label: string; amount_cents: number }[]) {
      if (charge.amount_cents === 0) continue
      total += charge.amount_cents
      lines.push({ name: charge.label, amount: money(charge.amount_cents) })
    }
    if (total <= 0) return { ok: false, reason: 'nothing_due' as const }

    const portalUrl = booking.confirmation_code
      ? `https://coffmancovelodging.com/my-stay?code=${encodeURIComponent(
          booking.confirmation_code,
        )}&email=${encodeURIComponent(booking.email)}`
      : null

    const { sendTemplateEmail } = await import('./email-templates/send-email')
    const result = await sendTemplateEmail('stay-payment-request', booking.email, {
      templateData: {
        name: booking.name ?? null,
        arrival: booking.arrival_date ?? null,
        departure: booking.departure_date ?? null,
        lines,
        total: money(total),
        confirmationCode: booking.confirmation_code ?? null,
        portalUrl,
        message: data.message ?? null,
      },
      idempotencyKey: `stay-payment-${booking.id}-${total}-${lines.length}`,
      replyTo: 'booking@coffmancovelodging.com',
    })

    const { logBookingEvent } = await import('./booking-events.server')
    await logBookingEvent(
      supabase,
      booking.id,
      'edited',
      `Payment request emailed — ${money(total)}`,
      String(userId),
    )

    return { ok: result.sent, reason: result.sent ? null : result.reason, total_cents: total }
  })

/** Records money taken in person: check, cash or a bank transfer. */
export const recordStayPayment = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        bookingId: z.string().uuid(),
        method: z.enum(['check', 'cash', 'ach']),
        amount_cents: z.number().int().min(1).max(100_000_000),
        reference: z.string().trim().max(120).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = await assertAdmin(context)

    const { data: order } = await supabase
      .from('shop_orders')
      .select('id,status')
      .eq('booking_request_id', data.bookingId)
      .maybeSingle()
    if (!order) return { ok: false, reason: 'no_order' as const }

    const { error } = await supabase
      .from('shop_orders')
      .update({ status: 'paid' })
      .eq('id', order.id)
    if (error) throw new Error(error.message)

    const label =
      data.method === 'check' ? 'Check' : data.method === 'cash' ? 'Cash' : 'Bank transfer'
    const { logBookingEvent } = await import('./booking-events.server')
    await logBookingEvent(
      supabase,
      data.bookingId,
      'paid',
      `${label} received — ${money(data.amount_cents)}${
        data.reference ? ` (${data.reference})` : ''
      }`,
      String(userId),
    )

    return { ok: true }
  })
